import type { CustomerCreatePayload } from "@/customTypings/customer/Customer";
import { authHeader, handleResponse } from "..";

export const CUSTOMER_CREATE_CONFLICT = {
    duplicateName: "customer.duplicateName",
    deletedRestoreAvailable: "customer.deletedRestoreAvailable",
    duplicateAmbiguous: "customer.duplicateAmbiguous",
} as const;

export type CustomerDeletedRestoreAvailableConflict = {
    code: string;
    message: string;
    existingId: string;
    existingName: string;
};

export function isCustomerDeletedRestoreAvailableConflict(
    value: unknown,
    expectedCode: string
): value is CustomerDeletedRestoreAvailableConflict {
    return (
        typeof value === "object" &&
        value !== null &&
        (value as CustomerDeletedRestoreAvailableConflict).code === expectedCode &&
        typeof (value as CustomerDeletedRestoreAvailableConflict).existingId === "string"
    );
}

export class CustomerCreateConflictError extends Error {
    readonly conflict: CustomerDeletedRestoreAvailableConflict;

    constructor(conflict: CustomerDeletedRestoreAvailableConflict) {
        super(conflict.message);
        this.name = "CustomerCreateConflictError";
        this.conflict = conflict;
    }
}

function siteCustomersBase(siteId: string) {
    return `/api/sites/${encodeURIComponent(siteId)}/customers`;
}

export async function postCustomerCreate<TDetail>(
    siteId: string,
    payload: CustomerCreatePayload
): Promise<TDetail> {
    const response = await fetch(`${siteCustomersBase(siteId)}/create`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    });
    const text = await response.text();
    let data: unknown = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        /* non-JSON */
    }

    if (
        response.status === 409 &&
        isCustomerDeletedRestoreAvailableConflict(data, CUSTOMER_CREATE_CONFLICT.deletedRestoreAvailable)
    ) {
        throw new CustomerCreateConflictError(data);
    }

    if (!response.ok) {
        const err = data as { code?: string; message?: string } | null;
        if (err?.code === CUSTOMER_CREATE_CONFLICT.duplicateName) {
            throw new Error(err.message ?? "A customer with this name already exists at this site.");
        }
        if (err?.code === CUSTOMER_CREATE_CONFLICT.duplicateAmbiguous) {
            throw new Error(err.message ?? "Multiple deleted customers match this name.");
        }
    }

    return handleResponse({
        text: async () => text,
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
    }) as Promise<TDetail>;
}
